import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { announcementComponents } from './registry';

/**
 * Global host for feature-flag announcements. Mounted once in the app shell.
 * Shows the first active/unseen announcement via the component registry and
 * fires the first-class trigger (records the view) the moment it is shown.
 */
export function AnnouncementHost() {
  const { user } = useAuth();
  const enabled = Boolean(user?.orgId);
  const { announcements, trigger, invalidate } = useAnnouncements(enabled);

  // Announcements dismissed this session — suppressed immediately without
  // waiting for the refetch (the recorded view keeps them gone on reload).
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(new Set());
  const current = announcements.find((a) => !dismissedIds.has(a.id));

  // First-class trigger: record the view exactly when we actually show it —
  // not on list fetch. Ref-guarded so it fires once per announcement.
  const triggeredIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (current && triggeredIdRef.current !== current.id) {
      triggeredIdRef.current = current.id;
      trigger(current.id).catch(() => {});
    }
  }, [current, trigger]);

  if (!current) return null;

  const Component = announcementComponents[current.uiComponent];
  if (!Component) return null;

  const handleDismiss = () => {
    setDismissedIds((prev) => new Set(prev).add(current.id));
    invalidate();
  };

  return <Component announcement={current} onDismiss={handleDismiss} />;
}
